// @flow

// This component is used in:

// (1) app/common/react/components/profile/ProductPins/ProductPinsCarousel.js,
// which should only show up for users in control groups for bizpro_web_pinner_view_ia and bizpro_web_self_view_ia.
// Both two experiments have ramped up to 100% for enabled groups, so it's just for safety purpose to keep
// this component, and it should get removed soon, after the two experiments end.

// (2) app/common/react/components/closeup/Deprecated/DeprecatedSTLCloseupCarousel.js
// which is used in brio closeup, and should be deprecated with brio closeup.

import React, { useState, useCallback } from 'react';
import ContentLayer from 'app/common/react/components/PinRep/components/ContentLayer';
import FadeableContainer from 'app/common/react/components/PinRep/components/FadeableContainer';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import PinTypeIdentifier from 'app/common/react/components/PinRep/components/PinTypeIdentifier';
import ProductPinMetaWrapper from 'app/common/react/components/shopping/ProductPin/ProductPinMetaWrapper';
import ProductSourceLink from 'app/common/react/components/shopping/ProductPin/ProductSourceLink';
import smartTruncate from 'app/common/lib/util/smartTruncate';
import truncateUrl from 'app/common/lib/util/truncateUrl';
import type { GridPin } from 'app/packages/pinterest-flow-types/pin';
import { getProductMerchant } from 'app/common/lib/CommerceUtil';
import { track } from 'app/common/lib/react/analytics';
import {
  PRODUCT_PIN_CHARACTER_COMPACT_LIMIT,
  PRODUCT_PIN_CHARACTER_LIMIT,
  PRODUCT_PIN_LETTER_WIDTH,
  PRODUCT_PIN_OVERLAY_OPACITY_LEGO,
  PRODUCT_PIN_COMPACT_SIZE,
  PRODUCT_PIN_HEIGHT_WIDTH_RATIO,
  PRODUCT_PIN_PADDING,
  PRODUCT_PIN_SIZE,
  PRODUCT_PIN_SIZE_LEGO,
  PRODUCT_PIN_SIZE_HEIGHT_LEGO,
  PRODUCT_PIN_WIDE_NUM,
} from 'app/common/react/components/shopping/ProductPin/consts';

import { Box, Image, Mask } from 'gestalt';

const SQUARE_SIZES = {
  '236x': 236,
  '474x': 474,
};

const styles = {
  visualOverlay: {
    backgroundColor: '#000',
    opacity: PRODUCT_PIN_OVERLAY_OPACITY_LEGO,
  },
};

// Use SHOW_DESCRIP_THRESHOLD_PX instead of compact or viewportSize
// otherwise we'll get unexpected display for
// Lego PinRep + non-core user experience
const SHOW_DESCRIP_THRESHOLD_PX = 120;

type Props = {|
  // An optional badge text to display over the Pin
  badgeText?: string,
  // Sizing flag for the product Pin image
  compact?: boolean,
  // The tagged object Pin to display
  pin: GridPin,
  // flag for lego experiment
  productPinView?: 'lego' | 'standard',
  // The optional on click callback
  onClick?: (SyntheticEvent<>) => void,
  // The width of container
  containerWidth?: number,
  // The link where product pin goes to after click
  link: string,
  // Whether to show overlay mask over the product pin
  showOverlay?: boolean,
  // Whether to show product price
  showPrice?: boolean,
  // Whether to show product indicator
  showProductIcon?: boolean,
  // Image size to display
  imageSizeKey?: '474x' | '236x',
  // Whether a squared product pin should be shown
  squared?: boolean,
  // Closeup layout description
  viewportSize?: 'narrow' | 'wide',
  // The product pin is in carousel or not
  inCarousel?: boolean,
  // For clickthrough logging
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
  componentType?: number,
  viewData?: Object,
|};

export default function ProductPin(props: Props) {
  const [isHovered, setHoverState] = useState(false);
  const handleMouseEnter = useCallback(() => setHoverState(true), [setHoverState]);
  const handleMouseLeave = useCallback(() => setHoverState(false), [setHoverState]);

  const {
    compact,
    containerWidth,
    link,
    pin,
    productPinView = 'standard',
    onClick,
    showProductIcon = false,
    imageSizeKey = '474x',
    squared = false,
    viewportSize,
  } = props;

  // Calculate box width and box height
  let boxWidth, boxHeight;

  if (squared) {
    // Check if set to squared
    boxWidth = SQUARE_SIZES[imageSizeKey];
    boxHeight = SQUARE_SIZES[imageSizeKey];
  } else if (containerWidth) {
    // Adapt product pin box size to container size
    boxWidth =
      (containerWidth - PRODUCT_PIN_PADDING * (PRODUCT_PIN_WIDE_NUM - 1)) / PRODUCT_PIN_WIDE_NUM;
    boxHeight = boxWidth * PRODUCT_PIN_HEIGHT_WIDTH_RATIO;
  } else {
    // Use default Product Pin Size
    const isLegoView = productPinView === 'lego';
    const productSize = isLegoView ? PRODUCT_PIN_SIZE_LEGO : PRODUCT_PIN_SIZE;
    boxWidth = compact ? PRODUCT_PIN_COMPACT_SIZE : productSize;
    boxHeight = isLegoView ? PRODUCT_PIN_SIZE_HEIGHT_LEGO : boxWidth;
  }

  const {
    description,
    domain,
    title,
    buyable_product: buyableProduct,
    id: pinId,
    link: directLink,
    rich_summary: richSummary,
    images: {
      [imageSizeKey]: { height, width, url },
    },
  } = pin;

  const pinTitle =
    title || (buyableProduct && buyableProduct.title) || (richSummary && richSummary.display_name);

  // Merchant name can be a brand name or a doamin like 'macys.com',
  // so truncateUrl is used instead of smart truncate
  const merchantName = getProductMerchant(pin);
  const truncatedMerchantName =
    merchantName &&
    truncateUrl(merchantName, {
      maxLength: boxWidth / PRODUCT_PIN_LETTER_WIDTH,
    });

  // Get length limit for product title
  const limit = compact ? PRODUCT_PIN_CHARACTER_COMPACT_LIMIT : PRODUCT_PIN_CHARACTER_LIMIT;

  // Log clickthrough event
  const handleClickthroughLogging = () => {
    const { componentType, viewData, viewType, viewParameter } = props;
    track(PCONST.thrift.EventType.PIN_CLICKTHROUGH, {
      component: componentType,
      objectId: pinId,
      view: viewType,
      viewParameter,
      viewData,
    });
  };

  return (
    <Box
      width={boxWidth}
      position="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box position="relative">
        <NavigateLink to={link} onClick={onClick}>
          <Mask shape="rounded" height={boxHeight} width={boxWidth} wash>
            <Image
              alt={pinTitle || description}
              naturalHeight={height}
              naturalWidth={width}
              src={url}
              fit="cover"
            />
            {isHovered && (
              <FadeableContainer visibility="visible">
                <ContentLayer additionalStyles={styles.visualOverlay} />
              </FadeableContainer>
            )}
          </Mask>
        </NavigateLink>
        <Box position="absolute" margin={2} left right top>
          {/* $FlowFixMe need to refine GridPin to Pin */}
          <PinTypeIdentifier pin={pin} allowShoppingPriceIndicator />
        </Box>
        {isHovered && (
          <Box display="flex" position="absolute" margin={3} left right bottom>
            {directLink && (
              <ProductSourceLink
                pinId={pinId}
                sourceUrl={directLink}
                domain={domain}
                sourceLinkStyle={viewportSize === 'narrow' ? 'icon-only' : 'default'}
                maxWidth={boxWidth}
                onClick={handleClickthroughLogging}
              />
            )}
          </Box>
        )}
      </Box>
      <NavigateLink to={link} onClick={onClick} stopPropagation>
        <ProductPinMetaWrapper
          buyableProduct={buyableProduct}
          hideAvailability={compact}
          pinDescription={(description && smartTruncate(description.trim(), limit)) || ''}
          pinTitle={(pinTitle && smartTruncate(pinTitle.trim(), limit)) || ''}
          richSummary={richSummary}
          merchantName={truncatedMerchantName}
          showProductIcon={showProductIcon}
          showDescription={boxWidth > SHOW_DESCRIP_THRESHOLD_PX}
        />
      </NavigateLink>
    </Box>
  );
}
